import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from '@/components/ui/use-toast';
import {
  DEFAULT_AUTO_TOPUP_AMOUNT_CENTS,
  DEFAULT_LOW_CREDIT_THRESHOLD,
  DEFAULT_TRIAL_CREDITS,
  type MapleOSProduct,
} from '@/components/billing/mapleosProducts';
import { getCheckoutReturnOrigin, openCheckoutUrl } from '@/lib/electronStripe';

export type AICreditLedgerEntry = {
  id: string;
  entry_type: string;
  credits_delta: number;
  amount_cents: number | null;
  token_count: number | null;
  provider: string | null;
  model: string | null;
  reference_id: string | null;
  metadata?: {
    source?: string | null;
    feature?: string | null;
    run_id?: string | null;
    run_title?: string | null;
    phase?: string | null;
    raw_chat_id?: string | null;
    requested_credits?: number | null;
    exceeded?: boolean;
  } | null;
  created_at: string;
};

export type AIUsageEvent = {
  id: string;
  provider: string;
  model: string;
  tokens_used: number;
  credits_used: number;
  source?: string | null;
  feature?: string | null;
  run_id?: string | null;
  run_title?: string | null;
  phase?: string | null;
  created_at: string;
};

export type MapleOSLicense = {
  id: string;
  sku: string;
  edition: string;
  version: string;
  includes_web_installer: boolean;
  includes_desktop_installer: boolean;
  includes_bare_metal_installer: boolean;
  status: string;
  created_at: string;
};

export type AICreditAccount = {
  balanceCredits: number;
  trialCreditsRemaining: number;
  availableCredits: number;
  lowCreditThreshold: number;
  autoTopupEnabled: boolean;
  autoTopupAmountCents: number;
  canAutoTopup: boolean;
  stripeCustomerId: string | null;
  low: boolean;
  exceeded: boolean;
  ledger: AICreditLedgerEntry[];
  usageEvents: AIUsageEvent[];
  licenses: MapleOSLicense[];
};

const DEFAULT_ACCOUNT: AICreditAccount = {
  balanceCredits: 0,
  trialCreditsRemaining: DEFAULT_TRIAL_CREDITS,
  availableCredits: DEFAULT_TRIAL_CREDITS,
  lowCreditThreshold: DEFAULT_LOW_CREDIT_THRESHOLD,
  autoTopupEnabled: false,
  autoTopupAmountCents: DEFAULT_AUTO_TOPUP_AMOUNT_CENTS,
  canAutoTopup: false,
  stripeCustomerId: null,
  low: false,
  exceeded: false,
  ledger: [],
  usageEvents: [],
  licenses: [],
};

function hasLiveCreditSnapshot(detail: unknown): detail is Partial<AICreditAccount> {
  if (!detail || typeof detail !== 'object') {
    return false;
  }

  return typeof (detail as { availableCredits?: unknown }).availableCredits === 'number';
}

export function useAICredits() {
  const { user, session } = useAuth();
  const [account, setAccount] = useState<AICreditAccount>(DEFAULT_ACCOUNT);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [topupLoading, setTopupLoading] = useState(false);
  const [productLoading, setProductLoading] = useState<string | null>(null);

  const fetchAccount = useCallback(async () => {
    if (!user) {
      setAccount(DEFAULT_ACCOUNT);
      return DEFAULT_ACCOUNT;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('get-ai-credit-account', {
        headers: {
          Authorization: `Bearer ${session?.access_token || ''}`,
        },
      });

      if (error) {
        throw error;
      }

      const next = {
        ...DEFAULT_ACCOUNT,
        ...data,
      } as AICreditAccount;

      setAccount(next);
      return next;
    } catch (error: any) {
      toast({
        title: 'Unable to load AI credits',
        description: error.message || 'Please try again.',
        variant: 'destructive',
      });
      return DEFAULT_ACCOUNT;
    } finally {
      setLoading(false);
    }
  }, [session?.access_token, user]);

  useEffect(() => {
    fetchAccount();
  }, [fetchAccount]);

  useEffect(() => {
    const refresh = (event?: Event) => {
      const detail = (event as CustomEvent<Partial<AICreditAccount>> | undefined)?.detail;

      if (hasLiveCreditSnapshot(detail)) {
        setAccount((current) => ({
          ...current,
          availableCredits: Number(detail.availableCredits ?? current.availableCredits),
          balanceCredits: typeof detail.balanceCredits === 'number' ? detail.balanceCredits : current.balanceCredits,
          trialCreditsRemaining: typeof detail.trialCreditsRemaining === 'number' ? detail.trialCreditsRemaining : current.trialCreditsRemaining,
          lowCreditThreshold: typeof detail.lowCreditThreshold === 'number' ? detail.lowCreditThreshold : current.lowCreditThreshold,
          autoTopupEnabled: typeof detail.autoTopupEnabled === 'boolean' ? detail.autoTopupEnabled : current.autoTopupEnabled,
          autoTopupAmountCents: typeof detail.autoTopupAmountCents === 'number' ? detail.autoTopupAmountCents : current.autoTopupAmountCents,
          canAutoTopup: typeof detail.canAutoTopup === 'boolean' ? detail.canAutoTopup : current.canAutoTopup,
          stripeCustomerId: typeof detail.stripeCustomerId === 'string' || detail.stripeCustomerId === null ? detail.stripeCustomerId : current.stripeCustomerId,
          low: typeof detail.low === 'boolean' ? detail.low : current.low,
          exceeded: typeof detail.exceeded === 'boolean' ? detail.exceeded : current.exceeded,
        }));
      }

      void fetchAccount();
    };

    window.addEventListener('mapleos:credits-refresh', refresh);
    return () => window.removeEventListener('mapleos:credits-refresh', refresh);
  }, [fetchAccount]);

  const updateSettings = useCallback(
    async (settings: {
      autoTopupEnabled: boolean;
      lowCreditThreshold: number;
      autoTopupAmountCents: number;
    }) => {
      if (!user) return;
      setSaving(true);
      try {
        const { data, error } = await supabase.functions.invoke('update-ai-credit-settings', {
          body: settings,
          headers: {
            Authorization: `Bearer ${session?.access_token || ''}`,
          },
        });

        if (error) throw error;

        const next = {
          ...DEFAULT_ACCOUNT,
          ...data,
        } as AICreditAccount;
        setAccount(next);
        window.dispatchEvent(new CustomEvent('mapleos:credits-refresh'));
        toast({ title: 'AI credit settings saved' });
      } catch (error: any) {
        toast({
          title: 'Could not save AI credit settings',
          description: error.message || 'Please try again.',
          variant: 'destructive',
        });
      } finally {
        setSaving(false);
      }
    },
    [session?.access_token, user]
  );

  const startTopupCheckout = useCallback(
    async (amountCents: number, savePaymentMethod: boolean) => {
      if (!user) return;
      setTopupLoading(true);
      try {
        const { data, error } = await supabase.functions.invoke('create-ai-credit-topup-checkout', {
          body: {
            amountCents,
            savePaymentMethod,
            origin: getCheckoutReturnOrigin(),
          },
          headers: {
            Authorization: `Bearer ${session?.access_token || ''}`,
          },
        });

        if (error) throw error;
        if (!data?.url) throw new Error('No checkout URL returned');
        await openCheckoutUrl(data.url);
      } catch (error: any) {
        toast({
          title: 'Could not start top-up',
          description: error.message || 'Please try again.',
          variant: 'destructive',
        });
      } finally {
        setTopupLoading(false);
      }
    },
    [session?.access_token, user]
  );

  const startProductCheckout = useCallback(
    async (product: MapleOSProduct) => {
      if (product.priceCents === 0) {
        window.location.href = '/start';
        return;
      }
      if (!user) {
        window.location.href = '/auth';
        return;
      }

      setProductLoading(product.id);
      try {
        const { data, error } = await supabase.functions.invoke('create-mapleos-product-checkout', {
          body: {
            sku: product.id,
            origin: getCheckoutReturnOrigin(),
          },
          headers: {
            Authorization: `Bearer ${session?.access_token || ''}`,
          },
        });

        if (error) throw error;
        if (!data?.url) throw new Error('No checkout URL returned');
        await openCheckoutUrl(data.url);
      } catch (error: any) {
        toast({
          title: 'Could not start checkout',
          description: error.message || 'Please try again.',
          variant: 'destructive',
        });
      } finally {
        setProductLoading(null);
      }
    },
    [session?.access_token, user]
  );

  const hasProductLicenses = useMemo(() => account.licenses.some((license) => license.status === 'active'), [account.licenses]);

  return {
    account,
    loading,
    saving,
    topupLoading,
    productLoading,
    hasProductLicenses,
    fetchAccount,
    updateSettings,
    startTopupCheckout,
    startProductCheckout,
  };
}